import {
  ComponentPropsWithoutRef,
  KeyboardEvent,
  MouseEvent,
  Ref,
  forwardRef,
} from "react";
import { useTrackingId } from "../../hooks";
import {
  DataTrackingId,
  ButtonAppearance,
  LayoutUtilProps,
  Size,
} from "../../types";
import Icon, { IconProps } from "../Icon";
import { childrenToString } from "../../internal/functions";
import { useLayoutPropsUtil } from "../../internal/hooks";
import Spinner from "../Spinner";

import cx from "classnames";
import styles from "../Button/Button.module.scss";

/**
 * Props for the ButtonLink component
 * @extends LayoutUtilProps
 * @extends ComponentPropsWithoutRef<"a">
 */
export type ButtonLinkProps = LayoutUtilProps &
  ComponentPropsWithoutRef<"a"> & {
    /**
     * The disabled state of the button link.
     * @default false
     */
    disabled?: boolean;

    /**
     * The visual variant of the button link.
     * @default secondary
     */
    appearance?: ButtonAppearance;

    /**
     * The size of the button link.
     * @default medium
     */
    size?: Extract<Size, "xsmall" | "small" | "medium" | "large">;

    /**
     * The icons of the button link.
     */
    icon?:
      | IconProps["svg"]
      | {
          after: IconProps["svg"];
        }
      | {
          before: IconProps["svg"];
        };

    /**
     * The loading state of the button link.
     * If true, it will show infinite state of the loading.
     * @default false
     */
    loading?: boolean;
  } &
  /**
   * Data tracking id
   */
  DataTrackingId;

/**
 * ButtonLink component for creating clickable links that look like buttons.
 *
 * Features:
 * - Multiple visual appearances (primary, secondary, ghost, danger variants)
 * - Four size options (xsmall, small, medium, large)
 * - Loading state with spinner animation
 * - Icon support (before, after, or icon-only)
 * - Full accessibility support with proper ARIA attributes
 * - Supports layout utilities for positioning and spacing
 * - Keyboard navigation support (Space and Enter keys)
 * - Automatic disabled state when loading
 * - Prevents navigation when disabled or loading
 * - Flexible content with text and/or icons
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <ButtonLink href="/dashboard" appearance="primary" size="medium">
 *   Go to Dashboard
 * </ButtonLink>
 */
export const ButtonLink = forwardRef(
  (props: ButtonLinkProps, ref: Ref<HTMLAnchorElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

    const {
      children,
      className,
      appearance = "secondary",
      size = "medium",
      icon,
      loading = false,
      disabled,
      onClick,
      onKeyUp,
      onKeyDown,
      style,
      href,
      type,
      ...rest
    } = componentProps;

    const data = {
      children: childrenToString(props.children),
      appearance,
      href,
      icon,
      size,
      type,
    };

    const iconBefore = (icon: ButtonLinkProps["icon"]) => {
      if (icon && typeof icon === "object" && "before" in icon) {
        return (
          <Icon
            aria-hidden
            className={styles["icon"]}
            inherit
            svg={icon.before}
          />
        );
      }
      if (icon && typeof icon !== "object") {
        return (
          <Icon aria-hidden className={styles["icon"]} inherit svg={icon} />
        );
      }
      return;
    };

    const handleOnClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (disabled || loading) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    const onKeyDownHandler = (e: KeyboardEvent<HTMLAnchorElement>) => {
      if (e.code === "Space") {
        e.preventDefault();
        onKeyDown?.(e);
        return;
      }

      onKeyDown?.(e);
    };

    const onKeyUpHandler = (e: KeyboardEvent<HTMLAnchorElement>) => {
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        if (onClick) {
          e.currentTarget.click();
        }
      }

      onKeyUp?.(e);
    };

    const trackingId = useTrackingId({
      name: "ButtonLink",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const buttonClassNames = cx(className, styles["button"], {
      [styles["appearance-primary"]]: appearance === "primary",
      [styles["appearance-secondary"]]: appearance === "secondary",
      [styles["appearance-ghost"]]: appearance === "ghost",
      [styles["danger-secondary"]]: appearance === "danger-secondary",
      [styles["danger-primary"]]: appearance === "danger",
      [styles["size-xsmall"]]: size === "xsmall",
      [styles["size-small"]]: size === "small",
      [styles["size-medium"]]: size === "medium",
      [styles["size-large"]]: size === "large",
      [styles["type-icon"]]: !children,
      [styles["loading"]]: loading,
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <a
        data-tracking-id={trackingId}
        className={buttonClassNames}
        aria-disabled={disabled || loading}
        aria-busy={loading}
        data-anv="button-link"
        tabIndex={disabled || loading ? -1 : 0}
        onClick={handleOnClick}
        onKeyDown={onKeyDownHandler}
        onKeyUp={onKeyUpHandler}
        style={styleCombined}
        ref={ref}
        href={href}
        {...rest}
      >
        {loading ? (
          <Spinner inherit size="small" className={styles["loading-spinner"]} />
        ) : icon ? (
          iconBefore(icon)
        ) : null}
        {children}
        {typeof icon === "object" && "after" in icon ? (
          <Icon
            className={styles["icon"]}
            inherit
            aria-hidden
            svg={icon.after}
          />
        ) : null}
      </a>
    );
  },
);

ButtonLink.displayName = "ButtonLink";
